/**
 * Mapping utilities for substantive procedures
 * Maps workspace names to their substantive route prefixes and execution sections
 */

// Map workspace names to their substantive route prefixes
export const WORKSPACE_TO_SUBSTANTIVE_PREFIX: Record<string, string> = {
  "Employee Benefits Expense": "payroll-substantive",
  "Property, Plant & Equipment": "ppe-substantive",
  "Intangible Assets": "ia-substantive",
  "Lease Liability": "ll-as-substantive",
  "Lease Liability - Ind AS": "ll-substantive",
  "Right-of-Use Assets": "rou-substantive",
  "Cost of Materials Consumed": "comc-substantive",
  "Trade Payables": "trdpbl-substantive",
  "Inventory": "inv-substantive",
  "Other Assets": "othas-substantive",
  "Other Financial Assets": "ofa-substantive",
  "Trade Receivables": "trdrcv-substantive",
  "Depreciation": "depr-substantive",
  "Impairment Testing": "imp-substantive",
  "Other Expenses": "othexp-substantive",
  "Borrowings": "bor-substantive",
  "Borrowings - Ind AS": "borindas-substantive",
  "Cash & Cash Equivalents": "cce-substantive",
  "Finance Cost": "fincst-substantive",
  "Income Tax": "inctx-substantive",
  "Investments": "invsmt-substantive",
  "Other Income": "othinc-substantive",
  "Revenue from Operations": "rfo-substantive",
  "Other Liabilities": "othlib-substantive",
  "Other Financial Liabilities": "ofllib-substantive",
  "Other Receivables": "othrec-substantive",
  "Provisions & Contingent Liabilities": "pcl-substantive",
  "Going Concern": "golib-substantive",
  "Litigations & Claims": "litclm-substantive",
  "Opening Balance Testing": "opnbal-substantive",
  "Related Party Disclosure": "relpard-substantive",
  "Equity": "eqt-substantive",
  "Management Use of Expert": "mgtexp-substantive",
  "Use of Auditor's Specialist": "useadtr-substantive",
  "Compliance with Laws and Regulations": "cmplr-substantive",
  "Review of Estimates for Management Bias": "rvestmb-substantive",
  "Internal Audit": "intadt-substantive",
  "Financial Closing & Reporting Process": "fcrp-substantive",
  "Management Override of Controls": "moc-substantive",
  "Biological Assets other than Bearer Plants": "bapb-substantive",
};

// Map substantive route prefixes to workspace names (reverse lookup)
export const SUBSTANTIVE_PREFIX_TO_WORKSPACE: Record<string, string> = Object.fromEntries(
  Object.entries(WORKSPACE_TO_SUBSTANTIVE_PREFIX).map(([workspace, prefix]) => [prefix, workspace])
);

// Map workspace names to their execution section routes
export const WORKSPACE_TO_EXECUTION_SECTION: Record<string, string> = {
  "Employee Benefits Expense": "execution-payroll",
  "Property, Plant & Equipment": "execution-ppe",
  "Intangible Assets": "execution-ia",
  "Lease Liability": "execution-lease-liability",
  "Lease Liability - Ind AS": "execution-lease-liability-ind-as",
  "Right-of-Use Assets": "execution-right-of-use-assets",
  "Cost of Materials Consumed": "execution-comc",
  "Trade Payables": "execution-trade-payables",
  "Inventory": "execution-inv",
  "Other Assets": "execution-other-assets",
  "Other Financial Assets": "execution-other-financial-assets",
  "Trade Receivables": "execution-trade-receivables",
  "Depreciation": "execution-depreciation",
  "Impairment Testing": "execution-impairment",
  "Other Expenses": "execution-other-expenses",
  "Borrowings": "execution-borrowings",
  "Borrowings - Ind AS": "execution-borrowings-ind-as",
  "Cash & Cash Equivalents": "execution-cce",
  "Finance Cost": "execution-fincst",
  "Income Tax": "execution-inctx",
  "Investments": "execution-invsmt",
  "Other Income": "execution-other-income",
  "Revenue from Operations": "execution-rfo",
  "Other Liabilities": "execution-other-liabilities",
  "Other Financial Liabilities": "execution-other-financial-liabilities",
  "Other Receivables": "execution-other-receivables",
  "Provisions & Contingent Liabilities": "execution-pcl",
  "Going Concern": "execution-going-concern",
  "Litigations & Claims": "execution-litigations-claims",
  "Opening Balance Testing": "execution-opening-balance-testing",
  "Related Party Disclosure": "execution-related-party-disclosure",
  "Equity": "execution-equity",
  "Management Use of Expert": "execution-management-use-of-expert",
  "Use of Auditor's Specialist": "execution-use-of-auditors-specialist",
  "Compliance with Laws and Regulations": "execution-compliance-with-laws-and-regulations",
  "Review of Estimates for Management Bias": "execution-review-of-estimates-for-management-bias",
  "Internal Audit": "execution-internal-audit",
  "Financial Closing & Reporting Process": "execution-fcrp",
  "Management Override of Controls": "execution-moc",
  "Biological Assets other than Bearer Plants": "execution-bapb",
};

/**
 * Get workspace name from substantive route prefix
 */
export function getWorkspaceFromSubstantivePrefix(prefix: string): string | null {
  return SUBSTANTIVE_PREFIX_TO_WORKSPACE[prefix] || null;
}

/**
 * Get execution section from workspace name
 */
export function getExecutionSectionFromWorkspace(workspaceName: string): string {
  return WORKSPACE_TO_EXECUTION_SECTION[workspaceName] || "";
}

/**
 * Get substantive prefix from workspace name
 */
export function getSubstantivePrefixFromWorkspace(workspaceName: string): string | null {
  return WORKSPACE_TO_SUBSTANTIVE_PREFIX[workspaceName] || null;
}

